"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import {
  type User as FirebaseUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { useHubSpotIdentify } from "@yardzen/next-client-util";
import { auth } from "@yfp/lib/firebase";
import { api } from "@yfp/lib/api-client";
import { ROUTES } from "@yfp/constants/routes";

export type YfpContractorData = {
  id: string;
  email: string;
  name: string;
  businessName: string;
  phone: string;
  city: string;
  profilePhotoUrl: string | null;
  subscriptionStatus: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
};

type SignupData = {
  email: string;
  password: string;
  name: string;
  businessName: string;
  phone: string;
  city: string;
  stripeCustomerId: string;
  paymentMethodId?: string;
};

type InitPaymentResponse = {
  clientSecret: string;
  stripeCustomerId: string;
};

type CheckoutSessionResponse = {
  sessionId: string;
  url: string;
};

type PortalSessionResponse = {
  url: string;
};

export type SubscriptionDetails = {
  subscriptionStatus: string;
  stripeSubscriptionId: string | null;
  stripePriceId: string | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
};

type AuthContextType = {
  firebaseUser: FirebaseUser | null;
  contractor: YfpContractorData | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Call BEFORE payment to create a Stripe customer + SetupIntent (no auth required). */
  initPayment: (email: string, name: string) => Promise<InitPaymentResponse>;
  /** Call AFTER SetupIntent succeeds to create Firebase user + contractor + subscription. */
  signup: (data: SignupData) => Promise<YfpContractorData>;
  logout: () => Promise<void>;
  createCheckoutSession: () => Promise<string>;
  createPortalSession: () => Promise<string>;
  refreshContractor: () => Promise<void>;
  fetchSubscriptionDetails: () => Promise<SubscriptionDetails | null>;
};

const AuthContext = createContext<AuthContextType | null>(null);

// Proxy through Next.js rewrites — no auth required
const API_PREFIX = "/api";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [contractor, setContractor] = useState<YfpContractorData | null>(null);
  const [loading, setLoading] = useState(true);

  // Guard: prevents onAuthStateChanged from calling fetchContractor while
  // the signup function is in the middle of creating the Firebase user +
  // contractor (the DB record doesn't exist yet at that point).
  const signingUpRef = useRef(false);

  const fetchContractor = useCallback(
    async (token?: string): Promise<YfpContractorData | null> => {
      try {
        const data = await api.get<YfpContractorData>("/yfp/auth/me", token);
        setContractor(data);
        return data;
      } catch (error) {
        console.error("Failed to fetch contractor data:", error);
        setContractor(null);
        return null;
      }
    },
    [],
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);

      if (user && !signingUpRef.current) {
        await fetchContractor();
      } else if (!user) {
        setContractor(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, [fetchContractor]);

  // Stitch this browser's HubSpot session to the authenticated contractor once
  // per session (guarded internally to avoid inflating page-view counts).
  useHubSpotIdentify(contractor?.email);

  const login = async (email: string, password: string) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const token = await result.user.getIdToken();
    await fetchContractor(token);
  };

  /**
   * Unauthenticated call — creates a Stripe customer + SetupIntent
   * so the frontend can collect payment details via Stripe Elements.
   */
  const initPayment = async (
    email: string,
    name: string,
  ): Promise<InitPaymentResponse> => {
    const response = await fetch(
      `${API_PREFIX}/yfp/subscription/init-payment`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name }),
      },
    );

    if (!response.ok) {
      const errorBody = await response.text();
      let message = "Failed to initialize payment. Please try again.";
      try {
        const parsed = JSON.parse(errorBody);
        if (parsed.message) message = parsed.message;
      } catch {
        // use default message
      }
      throw new Error(message);
    }

    return response.json();
  };

  /**
   * Called AFTER SetupIntent succeeds.
   * Creates the Firebase user, contractor record, and Stripe subscription
   * (the backend creates the subscription now that the contractor exists).
   */
  const signup = async (data: SignupData): Promise<YfpContractorData> => {
    signingUpRef.current = true;
    try {
      let token: string;
      try {
        const result = await createUserWithEmailAndPassword(
          auth,
          data.email,
          data.password,
        );
        token = await result.user.getIdToken();
      } catch (error: unknown) {
        const firebaseError = error as { code?: string };
        if (firebaseError.code === "auth/email-already-in-use") {
          throw new Error(
            "An account with this email already exists. Please log in or use a different email.",
          );
        }
        throw error;
      }

      await api.post(
        "/yfp/auth/signup",
        {
          name: data.name,
          businessName: data.businessName,
          phone: data.phone,
          city: data.city,
          stripeCustomerId: data.stripeCustomerId,
          paymentMethodId: data.paymentMethodId,
        },
        token,
      );

      const contractorData = await fetchContractor(token);
      if (!contractorData) {
        throw new Error("Failed to fetch contractor after signup");
      }
      return contractorData;
    } finally {
      signingUpRef.current = false;
    }
  };

  const logout = async () => {
    await firebaseSignOut(auth);
    setContractor(null);
  };

  const createCheckoutSession = async (): Promise<string> => {
    const { origin, pathname, search } = window.location;
    const response = await api.post<CheckoutSessionResponse>(
      "/yfp/subscription/checkout-session",
      {
        successUrl: `${origin}/signup/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}${pathname}${search}`,
      },
    );
    return response.url;
  };

  const createPortalSession = async (): Promise<string> => {
    const origin = window.location.origin;
    const response = await api.post<PortalSessionResponse>(
      "/yfp/subscription/portal-session",
      { returnUrl: `${origin}${ROUTES.dashboard}` },
    );
    return response.url;
  };

  const refreshContractor = async () => {
    await fetchContractor();
  };

  const fetchSubscriptionDetails =
    async (): Promise<SubscriptionDetails | null> => {
      try {
        return await api.get<SubscriptionDetails>("/yfp/subscription/status");
      } catch (error) {
        console.error("Failed to fetch subscription details:", error);
        return null;
      }
    };

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        contractor,
        loading,
        login,
        initPayment,
        signup,
        logout,
        createCheckoutSession,
        createPortalSession,
        refreshContractor,
        fetchSubscriptionDetails,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
